import React, { useEffect, useState } from "react";
import {
  Spin,
  Typography,
  Form,
  Input,
  Checkbox,
  Button,
  Row,
  Col,
  Card,
  message as antdMessage,
} from "antd";
import Swal from "sweetalert2";
import LeadService from "../../services/LeadService";
import "./style.css";

const { Title, Text } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [authorization, setAuthorization] = useState("");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [disableButton, setDisableButton] = useState(true);
  const [isChecked, setIsChecked] = useState(true);
  const [isLoan, setIsLoan] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const authParam = params.get("authorization");

    if (authParam) {
      setAuthorization(authParam);
      verifyAuthorizationHandler(authParam);
    }
  }, []);

  const verifyAuthorizationHandler = async (auth) => {
    setLoading(true);
    try {
      const result = await LeadService.verifyAuthorization(auth);
      if (!result.success) {
        Swal.fire("Oops!", result.message || "Failed to fetch", "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        const leadData = responseData.data;
        document.title = `${leadData.tbl_lead_category.name} | ${leadData.title}`;
        setData(leadData);
        setDisableButton(false);

        if (leadData.category_id === "5") {
          setIsLoan(true);
        }
      } else {
        setStatus(responseData.message);
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Request Failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!isChecked) {
      Swal.fire("Oops!", "Please accept the terms and conditions.", "error");
      return;
    }

    const { name, panNumber, mobile, email, pincode, loanAmount } = values;

    setLoading(true);
    try {
      const result = await LeadService.create(
        authorization,
        name,
        panNumber,
        mobile,
        email,
        pincode,
        loanAmount
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Failed to submit", "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        window.location.href = responseData.data.link;
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Request Failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container position-relative">
      {loading && (
        <div className="loader-overlay">
          <Spin size="large" tip="Loading..." />
        </div>
      )}

      <div className="bg-light py-5 px-3 d-flex align-items-center justify-content-center">
        <Card
          className="rounded-4 w-100"
          style={{ maxWidth: "1000px", minHeight: "800px" }}
          bodyStyle={{ padding: 24 }}
        >
          {status && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100vh" }}
            >
              <Text type="danger" strong style={{ fontSize: "22px" }}>
                {status}
              </Text>
            </div>
          )}

          {data && (
            <Row justify="center">
              <Col span={24} className="text-center mb-4">
                <img src={data.logo} alt="Logo" width="150" />
                <Title level={3} className="mt-3">
                  {data.title}
                </Title>
              </Col>

              <Col xs={24} sm={20} md={16} lg={12}>
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={handleSubmit}
                  initialValues={{ isChecked: true }}
                >
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your full name",
                      },
                    ]}
                  >
                    <Input placeholder="Full Name of the Applicant" />
                  </Form.Item>

                  <Form.Item
                    label="PAN Number"
                    name="panNumber"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your PAN number",
                      },
                      { len: 10, message: "PAN number must be 10 characters" },
                    ]}
                  >
                    <Input placeholder="PAN Number" maxLength={10} />
                  </Form.Item>

                  <Form.Item
                    label="Mobile Number"
                    name="mobile"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your mobile number",
                      },
                      {
                        pattern: /^[6-9]\d{9}$/,
                        message: "Enter a valid 10-digit mobile number",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Mobile Number (As per Aadhaar)"
                      maxLength={10}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Email ID"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email ID" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input placeholder="Email ID" />
                  </Form.Item>

                  <Form.Item
                    label="PIN Code"
                    name="pincode"
                    rules={[
                      { required: true, message: "Please enter your PIN code" },
                      { len: 6, message: "PIN code must be 6 digits" },
                    ]}
                  >
                    <Input placeholder="PIN Code" maxLength={6} />
                  </Form.Item>

                  {isLoan && (
                    <Form.Item
                      label="Required Loan Amount"
                      name="loanAmount"
                      rules={[
                        {
                          required: true,
                          message: "Please enter required loan amount",
                        },
                        {
                          validator: (_, value) =>
                            value > 0
                              ? Promise.resolve()
                              : Promise.reject("Amount must be greater than 0"),
                        },
                      ]}
                    >
                      <Input placeholder="Required Loan Amount" type="number" />
                    </Form.Item>
                  )}

                  <Form.Item>
                    <Checkbox
                      checked={isChecked}
                      onChange={() => setIsChecked(!isChecked)}
                    >
                      I authorize FISPL to securely store & use my data to
                      Call/SMS/Whatsapp/Email me about its products & accept the
                      terms and privacy policy.
                    </Checkbox>
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={disableButton}
                      block
                    >
                      Proceed to the Next Step
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
